// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button, Modal, Nav, Tab } from "react-bootstrap";
import { Site } from "states/sitestates";

import { ActionType, IAction, raiseAction, IActionLinkRfid } from "actions";
import { DataState } from "types";
import { IState } from "states/states";
import { IModalRfid, ModalPanel } from "states/guistates";
import { IRfid } from "states/ocppstates";
import { isSet } from "utils";
import { tabClickHandler } from "guiutils";

import { ReactComponent as IconLinked } from "static/iconLink.svg";
import { ReactComponent as IconUnLinked } from "static/iconUnlink.svg";

import SearchFilter from "view/comp/SearchFilter";

type IModalRfidLinkItemProps = IModalRfid & {
  isOpen: boolean;
  isReadOnly: boolean;
  dataState: DataState;
  rfid: IRfid | undefined;
  siteList: Site[]; // undefined if not loaded yet or something went wrong!
};

const mapStateToProps = (state: IState) => {
  const props: IModalRfidLinkItemProps = {
    ...state.gui.modalRfid!,
    isOpen: state.gui.modal.panel === ModalPanel.RFID_LINK,
    isReadOnly: false,
    dataState: state.gui.modal.dataState,
    rfid: state.ocpp.rfid.find((curItem) => curItem.id === state.gui.modalRfid?.id),
    siteList: state.site.siteList ?? [],
  };
  return props;
};

interface IModalRfidState {
  wasOpen: boolean;
  rfid: string;
  filterText: string;
  filterTextIsFocused: boolean;
  tableType: "linked" | "unlinked";
  linkedSites: number[];
}

interface IModalRfidItemDispatch {
  onSaveClick: (id: number | undefined, rfid: string, state: IModalRfidState) => void;
  onCancelClick: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IModalRfidItemDispatch = {
    onSaveClick: (id: number | undefined, rfid: string, state: IModalRfidState) => {
      // dispatch modify RFIDs now
      // get needded props from state
      const { linkedSites } = state;
      const payload: IActionLinkRfid = {
        id,
        rfid,
        siteIds: linkedSites,
      };
      dispatch(raiseAction(ActionType.ACT_SAVE_OCPP_RFID_REQ, payload));
    },
    onCancelClick: () => {
      dispatch(raiseAction(ActionType.ACT_MODAL_CANCEL));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msgLink: {
    id: "link charging card",
  },
  msgUnLink: {
    id: "unlink charging card",
  },
  msg_searchfield_placeholder: {
    id: "Type to filter sites...",
  },
});

class ModalRfidLinkItemComponent extends React.Component<
  IModalRfidLinkItemProps & IModalRfidItemDispatch & WrappedComponentProps,
  IModalRfidState
> {
  state: IModalRfidState = {
    wasOpen: false,
    rfid: "",
    filterText: "",
    filterTextIsFocused: false,
    tableType: "linked",
    linkedSites: [],
  };

  componentDidUpdate() {
    // check for changes in old value
    if (this.props.isOpen !== this.state.wasOpen) {
      if (this.props.isOpen) {
        // prepare settings according to properties!
        this.setState({
          wasOpen: this.props.isOpen,
          rfid: this.props.rfid?.rfid ?? this.props.addedrfid ?? "",
          linkedSites:
            isSet(this.props.rfid) && isSet(this.props.rfid.siteIds) ? this.props.rfid.siteIds : [],
        });
      } else {
        // remove state-settings for modal onclosing
        this.setState({
          wasOpen: this.props.isOpen,
          rfid: "",
          filterText: "",
          tableType: "linked",
          linkedSites: [],
        });
      }
    }
  }

  public render() {
    if (!this.props.isOpen) return <></>; // avoid rendering of inexisting data

    return (
      <Modal
        show={this.props.isOpen}
        onHide={this.props.onCancelClick}
        keyboard={!this.state.filterTextIsFocused}
      >
        <Modal.Header closeButton>
          <FormattedMessage id="Charging Card Links" />
        </Modal.Header>
        <Modal.Body>
          <>
            {this.renderMainSite()}
            <SearchFilter
              filterText={this.state.filterText}
              placeholder={this.props.intl.formatMessage(messages.msg_searchfield_placeholder)}
              onChange={(filterText) => {
                this.setState({ filterText });
              }}
              onFocus={(event) => this.setState({ filterTextIsFocused: true })}
              onBlur={(event) => this.setState({ filterTextIsFocused: false })}
            />
          </>
        </Modal.Body>
        <Modal.Body className="rfidModalBody">
          <Nav variant="tabs">
            <Nav.Item>
              <Nav.Link
                className={this.state.tableType === "linked" ? "active" : "inactive"}
                onClick={() => {
                  this.setState({
                    tableType: "linked",
                  });
                }}
              >
                <FormattedMessage id="Linked" />
                <span> ({String(this.getFilteredSiteItems("linked", true).length)})</span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                className={this.state.tableType === "unlinked" ? "active" : "inactive"}
                onClick={() => {
                  this.setState({
                    tableType: "unlinked",
                  });
                }}
              >
                <FormattedMessage id="Unlinked" />
                <span> ({String(this.getFilteredSiteItems("unlinked", true).length)})</span>
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <Tab.Content>{this.renderSiteList(this.state.tableType)}</Tab.Content>
        </Modal.Body>
        <Modal.Footer>
          <div className="button-bar-right">
            <Button
              id="cancelRfidSettings"
              variant="secondary"
              className="me-2"
              onClick={this.props.onCancelClick}
            >
              <FormattedMessage id="Cancel" />
            </Button>
            <Button
              id="saveRfidSettings"
              variant="primary"
              onClick={this.props.onSaveClick.bind(
                this,
                this.props.id,
                this.state.rfid,
                this.state,
              )}
              disabled={this.props.dataState === DataState.SAVE_REQ || !this.hasSiteListChanged()}
            >
              <FormattedMessage id="Save" />
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }

  private renderMainSite() {
    return (
      <span>
        <FormattedMessage id="Main site: " />
        {this.props.siteList.find((entry) => entry.id === this.props.rfid?.siteId)?.name}
      </span>
    );
  }

  private renderSiteList(type: "linked" | "unlinked") {
    const siteItems = this.getFilteredSiteItems(type).map((site) => {
      return (
        <tr key={site.id}>
          <td>{site.name}</td>
          <td className="icon-col img-link nowrap">
            <span
              tabIndex={0}
              onKeyDown={tabClickHandler}
              onClick={(_evt) => {
                const index = this.state.linkedSites.indexOf(site.id);
                const linkedSites = Array.from(this.state.linkedSites);

                type === "linked"
                  ? index > -1 && linkedSites.splice(index, 1)
                  : linkedSites.push(site.id);

                this.setState({ linkedSites });
              }}
              title={
                type === "linked"
                  ? this.props.intl.formatMessage(messages.msgUnLink)
                  : this.props.intl.formatMessage(messages.msgLink)
              }
            >
              {this.state.linkedSites.includes(site.id) ? (
                <IconLinked fill="green" />
              ) : (
                <IconUnLinked fill="red" />
              )}
            </span>
          </td>
        </tr>
      );
    });

    return (
      <table className="datatable rfidlink">
        <thead>
          <tr>
            <th>
              <FormattedMessage id="Site Name" />
            </th>
            {!this.props.isReadOnly && (
              <th className="icon-col">
                <FormattedMessage id="Site Linked" />
              </th>
            )}
          </tr>
        </thead>
        <tbody>{siteItems}</tbody>
      </table>
    );
  }

  private getFilteredSiteItems(type: "linked" | "unlinked", ignoreTextfilter = false) {
    return Array.from(this.props.siteList)
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter((site) => site.id !== this.props.rfid?.siteId)
      .filter((site) =>
        site.name
          .toLocaleLowerCase()
          .includes(ignoreTextfilter ? "" : this.state.filterText.toLocaleLowerCase()),
      )
      .filter(
        (site) =>
          (this.state.linkedSites.includes(site.id) && type === "linked") ||
          (!this.state.linkedSites.includes(site.id) && type === "unlinked"),
      );
  }

  private hasSiteListChanged() {
    return (
      !this.state.linkedSites.every((val) => this.props.rfid?.siteIds?.includes(val)) ||
      !this.props.rfid?.siteIds?.every((val) => this.state.linkedSites.includes(val))
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(ModalRfidLinkItemComponent));
